import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EditGameFormControl } from "../views/Game/EditGameFormControl";
import { createGame } from "../utils/common";
import { Game } from "../models";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(3),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "2px solid",
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  paddingBottom: theme.spacing(2),
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}));

const StyledStepper = styled(Stepper)({
  flex: 1,
  maxWidth: 400,
  "& .MuiStepLabel-label": {
    color: "text.secondary",
    fontSize: "0.875rem",
  },
  "& .MuiStepLabel-label.Mui-active": {
    color: "primary.main",
    fontWeight: 600,
  },
  "& .MuiStepLabel-label.Mui-completed": {
    color: "text.secondary",
  },
  "& .MuiStepIcon-root": {
    color: "text.secondary",
  },
  "& .MuiStepIcon-root.Mui-active": {
    color: "primary.main",
  },
  "& .MuiStepIcon-root.Mui-completed": {
    color: "primary.main",
  },
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingTop: theme.spacing(5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
  opacity: 0.9,
}));

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  "&.Mui-focused": {
    color: theme.palette.primary.main,
  },
})) as typeof FormLabel;

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  gap: theme.spacing(1),
}));

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: "1px solid",
  borderColor: selected
    ? theme.palette.primary.main
    : theme.palette.action.hover,
  backgroundColor: selected ? theme.palette.action.hover : "transparent",
  marginBottom: theme.spacing(1),
  transition: "all 0.2s",
  margin: 0,
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&.Mui-checked": {
    color: theme.palette.primary.main,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    border: "1px solid",
    borderColor: theme.palette.divider,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.action.selected,
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& .MuiFilledInput-input": {
    color: theme.palette.text.secondary,
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const CreateButton = styled(PrimaryButton)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
}));

interface CreateGameDialogProps {
  open: boolean;
  onClose: () => void;
  count?: number;
}

export function CreateGameDialog(props: CreateGameDialogProps) {
  const { open, onClose, count } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { themeConfig } = useAppTheme();

  const [activeStep, setActiveStep] = useState(0);
  const [newGame, setNewGame] = useState<Game | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<"1" | "2">("1");
  const [playerName, setPlayerName] = useState("");
  const [saveNickname, setSaveNickname] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Store the latest game settings (avoids async state issues)
  const gameSettingsRef = React.useRef<{
    title?: string;
    description?: string;
    expertMode?: boolean;
  }>({});

  const steps = [t("CreateGame.step1"), t("CreateGame.step2")];

  // Load saved nickname when dialog opens (if logged in)
  React.useEffect(() => {
    if (open) {
      const loadNickname = async () => {
        try {
          const { Auth } = await import("aws-amplify");
          await Auth.currentAuthenticatedUser();
          setIsLoggedIn(true);

          const { getPlayerNickname } = await import("../utils/playerState");
          const nickname = await getPlayerNickname();
          if (nickname) {
            setPlayerName(nickname);
            console.log("✅ Loaded saved nickname:", nickname);
          }
        } catch {
          setIsLoggedIn(false);
        }
      };
      loadNickname();
    }
  }, [open]);

  React.useEffect(() => {
    // Create game when dialog opens
    if (open && !newGame) {
      const initGame = async () => {
        const game = await createGame({ count }); // No navigate = returns game
        setNewGame(game as Game);

        // CRITICAL: Initialize ref with default values!
        // Otherwise if user doesn't edit, ref stays empty!
        gameSettingsRef.current = {
          title: game.title,
          description: game.description,
          expertMode: game.expertMode,
        };
        console.log(
          "✅ Initialized gameSettingsRef with defaults:",
          gameSettingsRef.current
        );
      };
      initGame();
    }
  }, [open, count, newGame]);

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleClose = () => {
    setActiveStep(0);
    setNewGame(null);
    onClose();
  };

  // ============================================================================
  // GAME START HANDLERS
  // ============================================================================

  /**
   * Save player information to localStorage
   */
  const savePlayerToLocalStorage = (
    gameId: string,
    playerNumber: number,
    playerName?: string
  ) => {
    localStorage.setItem(
      gameId,
      JSON.stringify({
        player: playerNumber,
        name: playerName || undefined,
      })
    );
  };

  /**
   * Build game update object with player and settings
   */
  const buildGameUpdates = async (
    newGame: Game,
    playerNumber: number,
    playerName: string,
    latestSettings: {
      title?: string;
      description?: string;
      expertMode?: boolean;
    }
  ): Promise<{ [key: string]: any }> => {
    console.log("🎮 [CreateGameDialog] Building game updates:", {
      newGame,
      playerNumber,
      playerName,
      latestSettings,
    });
    const { Auth } = await import("aws-amplify");

    // Ensure we always have valid values (fallback chain)
    const title = latestSettings.title ?? newGame.title ?? "";
    const description = latestSettings.description ?? newGame.description ?? "";
    const expertMode = latestSettings.expertMode ?? newGame.expertMode ?? false;

    const updates: any = {
      title,
      description,
      expertMode,
    };

    // Debug: Log the actual title value
    console.log("🎮 [CreateGameDialog] Title values:", {
      latestSettingsTitle: latestSettings.title,
      newGameTitle: newGame.title,
      finalTitle: title,
      updatesObject: updates,
    });

    // Set player name and userId
    if (playerNumber === 1) {
      updates.player1 = playerName;
      try {
        const user = await Auth.currentAuthenticatedUser();
        updates.player1UserId = user.attributes.sub;
        console.log("✅ Set player1UserId:", updates.player1UserId);
      } catch {
        console.log("ℹ️ Anonymous player 1");
      }
    } else if (playerNumber === 2) {
      updates.player2 = playerName;
      try {
        const user = await Auth.currentAuthenticatedUser();
        updates.player2UserId = user.attributes.sub;
        console.log("✅ Set player2UserId:", updates.player2UserId);
      } catch {
        console.log("ℹ️ Anonymous player 2");
      }
    }

    return updates;
  };

  /**
   * Update game in DynamoDB with player and settings
   */
  const updateGameInDatabase = async (
    gameId: string,
    updates: { [key: string]: any }
  ) => {
    try {
      const { updateGameInDB } = await import("../services/datastoreService");
      await updateGameInDB(gameId, updates);
      console.log("✅ Game settings and player assigned in DynamoDB:", updates);
    } catch (error) {
      console.error("❌ Failed to update game settings:", error);
      throw error; // Re-throw to let caller handle
    }
  };

  /**
   * Save nickname to user profile if requested
   */
  const saveNicknameIfNeeded = async (
    shouldSave: boolean,
    nickname: string
  ) => {
    if (!shouldSave || !nickname.trim()) {
      return;
    }

    try {
      const { savePlayerNickname } = await import("../utils/playerState");
      await savePlayerNickname(nickname.trim());
      console.log("✅ Nickname saved to profile");
    } catch (error) {
      console.error("❌ Failed to save nickname:", error);
      // Don't throw - nickname save failure shouldn't block game start
    }
  };

  /**
   * Main handler: Start game with selected player
   */
  const handleStartGame = async () => {
    if (!newGame) {
      return;
    }

    const playerNumber = parseInt(selectedPlayer);
    const name =
      playerName ||
      t(
        playerNumber === 1
          ? "CreateGameDefaults.player1"
          : "CreateGameDefaults.player2"
      );

    console.log("🎮 [CreateGameDialog] Starting game:", {
      playerNumber,
      playerName,
      finalName: name,
      gameId: newGame.id,
    });

    // 1. Save to localStorage
    savePlayerToLocalStorage(newGame.id, playerNumber, playerName);

    // 2. Build and apply game updates
    try {
      const latestSettings = gameSettingsRef.current;
      const updates = await buildGameUpdates(
        newGame,
        playerNumber,
        name,
        latestSettings
      );
      await updateGameInDatabase(newGame.id, updates);
    } catch (error) {
      // Log error but continue - user can still navigate to game
      console.error("⚠️ Game update failed, but continuing:", error);
    }

    // 3. Save nickname if requested
    await saveNicknameIfNeeded(saveNickname, playerName);

    // 4. Navigate to game
    navigate(`/game/${newGame.id}`);
    handleClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: themeConfig.dialog.background,
          border: themeConfig.dialog.border,
        },
      }}
    >
      <StyledDialogTitle
        sx={{
          bgcolor: themeConfig.dialog.headerBackground,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {t("CreateGame.titleShort")}
        </Typography>

        {/* Stepper inline */}
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>

        <IconButton onClick={handleClose} sx={{ color: "primary.main" }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Step Content */}
        {activeStep === 0 && newGame && (
          <Box>
            <SectionTitle variant="h6" gutterBottom>
              {t("CreateGame.configureGame")}
            </SectionTitle>
            <SectionDescription variant="body2">
              {t("CreateGame.configureGameHelper")}
            </SectionDescription>
            <EditGameFormControl
              game={newGame}
              updateGame={(data) => {
                // Update the local game state
                setNewGame({ ...newGame, ...data } as Game);
                // ALSO save to ref (avoids async state issues)
                gameSettingsRef.current = {
                  ...gameSettingsRef.current,
                  ...data,
                };
                console.log(
                  "📝 Game settings updated:",
                  gameSettingsRef.current
                );
              }}
              hideEdit={() => {}} // Don't close anything
              hideSubmitButton={true} // Hide the submit button in dialog
            />
          </Box>
        )}

        {activeStep === 1 && newGame && (
          <Box>
            <SectionTitle variant="h6" gutterBottom>
              {t("CreateGame.choosePlayer")}
            </SectionTitle>
            <SectionDescription variant="body2">
              {t("CreateGame.choosePlayerHelper")}
            </SectionDescription>

            {/* Improved Player Selection with Radio Buttons */}
            <Box>
              <FormControl component="fieldset" fullWidth sx={{ border: 0 }}>
                <StyledFormLabel component="legend">
                  {t("CreateGame.selectPlayerLabel")}
                </StyledFormLabel>
                <StyledRadioGroup
                  value={selectedPlayer}
                  onChange={(e) =>
                    setSelectedPlayer(e.target.value as "1" | "2")
                  }
                >
                  <StyledFormControlLabel
                    value="1"
                    selected={selectedPlayer === "1"}
                    control={<StyledRadio />}
                    label={
                      <Typography color="text.primary">
                        {t("CreateGame.player1")}
                      </Typography>
                    }
                  />
                  <StyledFormControlLabel
                    value="2"
                    selected={selectedPlayer === "2"}
                    control={<StyledRadio />}
                    label={
                      <Typography color="text.primary">
                        {t("CreateGame.player2")}
                      </Typography>
                    }
                  />
                </StyledRadioGroup>

                <StyledTextField
                  label={t("CreateGame.playerNameOptional")}
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  variant="outlined"
                  fullWidth
                />

                {/* Save Nickname Checkbox (only for logged-in users) */}
                {isLoggedIn && playerName.trim() && (
                  <Tooltip
                    title={t("CreateGame.saveNicknameTooltip")}
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveNickname}
                          onChange={(e) => setSaveNickname(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t("CreateGame.saveNickname")}
                      sx={{ mt: 1 }}
                    />
                  </Tooltip>
                )}
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box display="flex" justifyContent="space-between" mt={4}>
          {activeStep === 0 ? (
            <>
              <SecondaryButton onClick={handleClose}>
                {t("PersonalSettings.cancel")}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleNext}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                {t("CreateGame.next")}
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                {t("CreateGame.back")}
              </SecondaryButton>
              <CreateButton
                onClick={handleStartGame}
                variant="contained"
                endIcon={<CheckIcon />}
              >
                {t("CreateGame.createGame")}
              </CreateButton>
            </>
          )}
        </Box>
      </StyledDialogContent>
    </StyledDialog>
  );
}
